import React, { useCallback, useEffect, useState } from "react";
import { Copy, Plus, RefreshCw, Send, Trash2 } from "lucide-react";
import { StatusPill, errorText, formatWhen } from "./common";

// Outgoing webhook endpoints (Master Administrator only):
// /api/webhook-endpoints (outgoing_webhooks.py). The signing secret is
// returned once, at creation (or rotation), and shown here exactly once.

export default function WebhookEndpoints({ api, showNotice, onChanged }) {
  const [data, setData] = useState({ endpoints: [], events: [] });
  const [form, setForm] = useState(null);
  const [secret, setSecret] = useState(null);
  const [selected, setSelected] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [detail, setDetail] = useState(null);
  const [testResults, setTestResults] = useState({});

  const load = useCallback(async () => {
    try { setData((await api.get("/webhook-endpoints")).data); } catch (err) { showNotice(errorText(err, "Failed to load webhooks"), "error"); }
  }, [api, showNotice]);
  useEffect(() => { load(); }, [load]);

  const loadDeliveries = useCallback(async (id, status) => {
    try { setDeliveries((await api.get(`/webhook-endpoints/${id}/deliveries`, { params: { status: status || undefined, limit: 50 } })).data.deliveries || []); } catch (err) { showNotice(errorText(err, "Failed to load deliveries"), "error"); }
  }, [api, showNotice]);
  useEffect(() => { if (selected) loadDeliveries(selected, statusFilter); }, [selected, statusFilter, loadDeliveries]);

  const changed = async () => { await load(); if (onChanged) onChanged(); };

  const create = async () => {
    try {
      const res = await api.post("/webhook-endpoints", { name: form.name, url: form.url, events: form.allEvents ? ["*"] : form.events, enabled: true });
      setSecret({ name: res.data.endpoint.name, value: res.data.secret });
      setForm(null);
      await changed();
    } catch (err) { showNotice(errorText(err, "Could not create the endpoint"), "error"); }
  };
  const run = async (fn, okMessage) => { try { await fn(); if (okMessage) showNotice(okMessage, "success"); await changed(); if (selected) await loadDeliveries(selected, statusFilter); } catch (err) { showNotice(errorText(err, "Action failed"), "error"); } };
  const sendTest = async (ep) => {
    try {
      const res = await api.post(`/webhook-endpoints/${ep.id}/test`);
      setTestResults((r) => ({ ...r, [ep.id]: res.data }));
      showNotice(res.data.ok ? `Test delivered (${res.data.status_code}, ${res.data.latency_ms} ms)` : `Test failed: ${res.data.error}`, res.data.ok ? "success" : "error");
      if (selected === ep.id) loadDeliveries(ep.id, statusFilter);
    } catch (err) { showNotice(errorText(err, "Test failed"), "error"); }
  };
  const toggleEvent = (type) => setForm((f) => ({ ...f, events: f.events.includes(type) ? f.events.filter((e) => e !== type) : [...f.events, type] }));

  return (
    <section className="panel glass-panel" data-testid="webhook-endpoints">
      <div className="panel-header">
        <h3>Outgoing webhooks</h3>
        <span>
          <button className="ghost-btn small" onClick={load}><RefreshCw size={14} /> Refresh</button>
          <button className="primary-btn small" data-testid="webhook-new" onClick={() => setForm({ name: "", url: "https://", allEvents: true, events: [] })}><Plus size={14} /> Add endpoint</button>
        </span>
      </div>
      <p className="muted">
        Events are POSTed as JSON to your HTTPS URL with <code>X-MSB-Signature: t=&lt;unix&gt;,v1=&lt;hex&gt;</code> where v1 = HMAC-SHA256(secret, "&lt;t&gt;.&lt;raw body&gt;") and an <code>X-MSB-Timestamp</code> header.
        Reject deliveries whose timestamp is older than 5 minutes. Failed deliveries retry with backoff (up to {data.max_attempts || 6} attempts); an endpoint is auto-disabled after {data.auto_disable_after || 10} consecutive failures.
        Only public https:// URLs are allowed (private/internal addresses are blocked).
      </p>

      {secret && (
        <div className="panel" style={{ padding: 10, marginBottom: 10, border: "1px solid #fbbf24" }} data-testid="webhook-secret">
          <strong>Signing secret for "{secret.name}" - copy it now, it will not be shown again.</strong>
          <div className="compact-row"><code style={{ wordBreak: "break-all" }}>{secret.value}</code>
            <button className="ghost-btn small" onClick={() => { if (navigator.clipboard) navigator.clipboard.writeText(secret.value); showNotice("Secret copied", "success"); }}><Copy size={14} /> Copy</button>
            <button className="ghost-btn small" onClick={() => setSecret(null)}>I have saved it</button>
          </div>
        </div>
      )}

      {form && (
        <div className="panel" style={{ padding: 10, marginBottom: 10 }} data-testid="webhook-form">
          <div className="form-grid">
            <label>Name<input data-testid="webhook-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
            <label>URL (https)<input data-testid="webhook-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} /></label>
          </div>
          <label><input type="checkbox" checked={form.allEvents} onChange={(e) => setForm({ ...form, allEvents: e.target.checked })} /> Send every event</label>
          {!form.allEvents && (
            <div>{data.events.map((ev) => <label key={ev.type} style={{ display: "inline-block", marginRight: 12 }}><input type="checkbox" checked={form.events.includes(ev.type)} onChange={() => toggleEvent(ev.type)} /> {ev.label}</label>)}</div>
          )}
          <button className="primary-btn" data-testid="webhook-create" onClick={create}>Create endpoint</button> <button className="ghost-btn" onClick={() => setForm(null)}>Cancel</button>
        </div>
      )}

      <div className="compact-list">
        {data.endpoints.map((ep) => (
          <div key={ep.id} className="panel" style={{ padding: 10, marginBottom: 8 }} data-testid={`webhook-${ep.id}`}>
            <div className="compact-row">
              <StatusPill status={ep.status} />
              <div>
                <strong>{ep.name}</strong> <span>{ep.url}</span>
                <div className="muted">{(ep.events || []).join(", ")} • secret {ep.secret_hint} • {ep.consecutive_failures} consecutive failures{ep.last_delivery_at ? ` • last delivery ${formatWhen(ep.last_delivery_at)}${ep.last_status_code ? ` (${ep.last_status_code})` : ""}` : ""}</div>
                {ep.disabled_reason && <div style={{ color: "#f87171" }}>{ep.disabled_reason}</div>}
                {testResults[ep.id] && <div className="muted">Last test: {testResults[ep.id].ok ? `OK ${testResults[ep.id].status_code} in ${testResults[ep.id].latency_ms} ms` : `failed - ${testResults[ep.id].error}`}</div>}
              </div>
            </div>
            <div className="compact-row">
              <button className="ghost-btn small" data-testid={`webhook-test-${ep.id}`} onClick={() => sendTest(ep)}><Send size={14} /> Send test event</button>
              <button className="ghost-btn small" onClick={() => run(() => api.put(`/webhook-endpoints/${ep.id}`, { enabled: !ep.enabled }), ep.enabled ? "Endpoint disabled" : "Endpoint enabled")}>{ep.enabled ? "Disable" : "Enable"}</button>
              <button className="ghost-btn small" onClick={() => run(async () => { const r = await api.post(`/webhook-endpoints/${ep.id}/rotate-secret`); setSecret({ name: ep.name, value: r.data.secret }); }, "Secret rotated")}>Rotate secret</button>
              <button className="ghost-btn small" onClick={() => setSelected(selected === ep.id ? null : ep.id)}>{selected === ep.id ? "Hide log" : "Delivery log"}</button>
              <button className="ghost-btn small" aria-label="Delete endpoint" onClick={() => { if (window.confirm(`Delete ${ep.name}?`)) run(() => api.delete(`/webhook-endpoints/${ep.id}`), "Endpoint deleted"); }}><Trash2 size={14} /></button>
            </div>
            {selected === ep.id && (
              <div data-testid="webhook-deliveries">
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="">All deliveries</option><option value="pending">Pending</option><option value="succeeded">Succeeded</option><option value="failed">Failed</option><option value="cancelled">Cancelled</option>
                </select>
                {deliveries.map((d) => (
                  <div key={d.id}>
                    <div className="compact-row" style={{ cursor: "pointer" }} onClick={() => setDetail(detail?.id === d.id ? null : d)}>
                      <StatusPill status={d.status} />
                      <span>{d.event_type}{d.test ? " (test)" : ""} • {d.last_status_code ?? "-"} • {d.last_latency_ms ?? "-"} ms • attempt {d.attempts}/{d.max_attempts} • {formatWhen(d.created_at)}{d.last_error ? ` • ${d.last_error}` : ""}{d.status === "pending" ? ` • next ${formatWhen(d.next_attempt_at)}` : ""}</span>
                      {["failed", "cancelled"].includes(d.status) && !d.test && <button className="ghost-btn small" onClick={(e) => { e.stopPropagation(); run(() => api.post(`/webhook-deliveries/${d.id}/retry`), "Re-queued"); }}>Retry</button>}
                    </div>
                    {detail?.id === d.id && <pre className="panel" style={{ padding: 8, overflow: "auto" }}>{JSON.stringify(d.attempt_log, null, 2)}</pre>}
                  </div>
                ))}
                {!deliveries.length && <div className="empty-cell">No deliveries yet.</div>}
              </div>
            )}
          </div>
        ))}
        {!data.endpoints.length && <div className="empty-cell">No webhook endpoints yet.</div>}
      </div>
    </section>
  );
}
